import React, { useEffect } from 'react'
import { toast } from 'react-toastify'
import InfoIcon from '@mui/icons-material/Info'
import { Box, Grid, MenuItem, Select, TextField, Tooltip } from '@mui/material'
import Button from '@mui/material/Button'
import { styled } from '@mui/material/styles'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { DesktopDatePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useMutation } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import RewardsAPI from 'api/faber/RewardsAPI'
import { Form, Formik } from 'formik'
import OrbitSession from 'hooks/useCookies'
import * as Yup from 'yup'

import NormalText from '../../../../common/Typography/NormalText'

const CustomDatePicker = (props: DatePickerProps) => {
  const { label, disabled, date, setDate } = props

  const handleStartDate = (newValue: Date | null) => {
    setDate(newValue)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DesktopDatePicker
        label={label}
        inputFormat='dd/MM/yyyy'
        value={date}
        onChange={handleStartDate}
        renderInput={(params) => (
          <TextField placeholder='Date' variant='standard' {...params} />
        )}
        disabled={disabled}
      />
    </LocalizationProvider>
  )
}

export default CustomDatePicker

interface DatePickerProps {
  label: string
  disabled: boolean
  date: Date | null
  setDate: (value: Date | null) => void
}

const formikInitialValues = {
  rewardName: '',
  rewardDescription: '',
  rewardDescriptionInternal: '',
  benefitTypeId: [],
  rewardRuleId: []
}

const NewRewardProductSchema = Yup.object().shape({
  rewardName: Yup.string().required('Reward product name is required'),
  rewardDescription: Yup.string().required(
    'Reward product description is required'
  ),
  rewardDescriptionInternal: Yup.string().required(
    'Reward product internal description is required'
  )
})

const StyledTextField = styled(TextField)({
  '& input': {
    padding: '6px'
  },
  width: '100%'
})

export const NewRewardProductForm = ({
  handleClose
}: {
  handleClose: () => void
}) => {
  const faberToken = OrbitSession.get('faberToken')
  const { data: benefitTypes } = useQuery(['getBenefits'], async () =>
    RewardsAPI.getBenefits(faberToken)
  )

  const { mutate: createRewardProduct } = useMutation(
    RewardsAPI.createRewardProduct,
    {
      onSuccess: () => {
        toast.success('Reward product created successfully')
        handleClose()
      },
      onError: () => {
        toast.error('Error creating reward product')
      }
    }
  )

  interface BenefitRow {
    id: string
    benefit_summary: string
    benefit_value: string
    benefit_unit: string
  }
  const [benefitRows, setBenefitRows] = React.useState<BenefitRow[]>([])
  const [benefitSelectionModel, setBenefitSelectionModel] = React.useState<
    string[]
  >([])

  const columns: GridColDef[] = [
    {
      field: 'benefit_summary',
      headerName: 'Benefit Name',
      width: 350
    },
    {
      field: 'benefit_value',
      headerName: 'Value',
      width: 350
    },
    {
      field: 'benefit_unit',
      headerName: 'Unit',
      width: 350
    }
  ]

  useEffect(() => {
    if (benefitTypes) {
      setBenefitRows(benefitTypes)
    }
  }, [benefitTypes])

  const rowIdToBenefitId = benefitRows.reduce((acc, row) => {
    acc[row.id] = row.id
    return acc
  }, {})

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleFormSubmit = (values: NewRewardProductFormikProps) => {
    const allValues = {
      rewardName: values.rewardName,
      rewardDescription: values.rewardDescription,
      rewardDescriptionInternal: values.rewardDescriptionInternal,
      benefitTypeId: benefitSelectionModel,
      rewardRuleId: values.rewardRuleId,
      faberToken: faberToken
    }
    createRewardProduct(allValues)
  }

  /**
   * Event handlers
   */
  const handleUsageTypeSelect = (
    e,
    setFieldValue: (
      field: string,
      value,
      shouldValidate?: boolean | undefined
    ) => void
  ) => {
    if (Array.isArray(e.target.value)) {
      setFieldValue(e.target.name, e.target.value)
    } else {
      setFieldValue(e.target.name, [e.target.value])
    }
  }

  return (
    <Formik
      initialValues={formikInitialValues}
      onSubmit={handleFormSubmit}
      validationSchema={NewRewardProductSchema}
    >
      {({
        submitForm,
        handleChange,
        errors,
        touched,
        handleBlur,
        setFieldValue,
        values
      }) => (
        <Form>
          <Grid container rowSpacing={2}>
            {/* REWARD PRODUCT NAME */}
            <Grid item xs={3}>
              <NormalText isBold>Name</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='rewardName'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'reward-name'
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.rewardName && touched.rewardName)}
                  helperText={
                    errors.rewardName && touched.rewardName
                      ? errors.rewardName
                      : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <NormalText isBold>Description</NormalText>
                <Tooltip title='Might be displayed to customers'>
                  <InfoIcon style={{ marginLeft: '5px' }} />
                </Tooltip>
              </div>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  inputProps={{
                    'data-testid': 'reward-description'
                  }}
                  name='rewardDescription'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  fullWidth
                  variant='outlined'
                  error={
                    !!(errors.rewardDescription && touched.rewardDescription)
                  }
                  helperText={
                    errors.rewardDescription && touched.rewardDescription
                      ? errors.rewardDescription
                      : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Internal Description</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  inputProps={{
                    'data-testid': 'reward-description-internal'
                  }}
                  name='rewardDescriptionInternal'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  fullWidth
                  variant='outlined'
                  error={
                    !!(
                      errors.rewardDescriptionInternal &&
                      touched.rewardDescriptionInternal
                    )
                  }
                  helperText={
                    errors.rewardDescriptionInternal &&
                    touched.rewardDescriptionInternal
                      ? errors.rewardDescriptionInternal
                      : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <NormalText isBold>Benefits</NormalText>
                <Tooltip title='Can have more than 1 benefits'>
                  <InfoIcon style={{ marginLeft: '5px' }} />
                </Tooltip>
              </div>
            </Grid>
            <Grid item xs={12} height={640}>
              <DataGrid
                rows={benefitRows}
                columns={columns}
                checkboxSelection
                rowsPerPageOptions={[10]}
                pageSize={10}
                selectionModel={benefitSelectionModel}
                onSelectionModelChange={(newSelection) => {
                  const selectedBenefitIds = newSelection.map(
                    (rowId) => rowIdToBenefitId[rowId]
                  )
                  setBenefitSelectionModel(selectedBenefitIds)
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Reward Rules</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Select
                variant='standard'
                sx={{ width: '80%' }}
                onChange={(e) => handleUsageTypeSelect(e, setFieldValue)}
                name='rewardRuleId'
                label=''
                multiple
                value={values.rewardRuleId}
              >
                <MenuItem value='0'>SGP</MenuItem>
                <MenuItem value='1'>USA</MenuItem>
              </Select>
            </Grid>
          </Grid>
          <Grid>
            <Box mt={3}>
              {' '}
              {/* Adjust the number to increase or decrease the space */}
              <Button onClick={submitForm}>Save changes</Button>
            </Box>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}
interface NewRewardProductFormikProps {
  rewardName: string
  rewardDescription: string
  rewardDescriptionInternal: string
  benefitTypeId: string[]
  rewardRuleId: string[]
}
